import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import logo from '../assets/images/logo-with-tm.png';
import {
  IoAlertCircle,
  IoCloseCircle,
  IoPlayCircle,
} from 'react-icons/io5';

// ─── SVG helpers ────────────────────────────────────────────────────────────

const CX = 160, CY = 160, INNER_R = 60, OUTER_R = 150, LABEL_R = 107;

const toRad = (deg) => (deg - 90) * (Math.PI / 180);

const createRadialSectionPath = (cx, cy, innerR, outerR, startAngle, endAngle) => {
  const s = toRad(startAngle);
  const e = toRad(endAngle);
  const x1o = cx + outerR * Math.cos(s), y1o = cy + outerR * Math.sin(s);
  const x2o = cx + outerR * Math.cos(e), y2o = cy + outerR * Math.sin(e);
  const x1i = cx + innerR * Math.cos(s), y1i = cy + innerR * Math.sin(s);
  const x2i = cx + innerR * Math.cos(e), y2i = cy + innerR * Math.sin(e);
  const large = endAngle - startAngle > 180 ? 1 : 0;
  return [
    `M ${x1i} ${y1i}`,
    `L ${x1o} ${y1o}`,
    `A ${outerR} ${outerR} 0 ${large} 1 ${x2o} ${y2o}`,
    `L ${x2i} ${y2i}`,
    `A ${innerR} ${innerR} 0 ${large} 0 ${x1i} ${y1i}`,
    'Z',
  ].join(' ');
};

const getLabelPos = (centerAngle) => ({
  x: CX + LABEL_R * Math.cos(toRad(centerAngle)),
  y: CY + LABEL_R * Math.sin(toRad(centerAngle)),
});

// Fixed angle metadata for the 8 sections (same regardless of handedness)
const SECTIONS_META = [
  { id: 'high',      start: 337.5, end: 22.5,  centerAngle: 0   },
  { id: 'highRight', start: 22.5,  end: 67.5,  centerAngle: 45  },
  { id: 'right',     start: 67.5,  end: 112.5, centerAngle: 90  },
  { id: 'lowRight',  start: 112.5, end: 157.5, centerAngle: 135 },
  { id: 'low',       start: 157.5, end: 202.5, centerAngle: 180 },
  { id: 'lowLeft',   start: 202.5, end: 247.5, centerAngle: 225 },
  { id: 'left',      start: 247.5, end: 292.5, centerAngle: 270 },
  { id: 'highLeft',  start: 292.5, end: 337.5, centerAngle: 315 },
];

// ─── Diagnostic data (ported from RN) ───────────────────────────────────────

const getTargetSections = (hand) => {
  const r = hand === 'right';
  return [
    {
      id: 'high',
      label: 'Breaking Wrist Up',
      shortLabel: 'High',
      reason: 'Most likely caused by breaking your wrist upward or anticipating recoil by pulling up.',
      recommendations: [
        'Keep wrists locked and firm',
        'Focus on follow-through',
        'Practice dry fire with consistent wrist position',
        'Check your sight alignment before each shot',
      ],
    },
    {
      id: 'highRight',
      label: r ? 'Heeling (Anticipating Recoil)' : 'Tightening Grip While Pulling Trigger',
      shortLabel: r ? 'High\nRight' : 'High\nLeft',
      reason: r
        ? 'Often caused by anticipating recoil and pushing the heel of your palm forward.'
        : 'Usually from tightening your grip as you pull the trigger.',
      recommendations: [
        'Relax your grip slightly',
        'Practice smooth trigger press',
        'Focus on surprise break',
        'Use ball-and-dummy drills',
      ],
    },
    {
      id: 'right',
      label: r
        ? 'Squeezing Thumb or Too Much Fingertip on Trigger'
        : 'Too Little Fingertip on Trigger',
      shortLabel: r ? 'Right' : 'Left',
      reason: r
        ? 'Caused by too much finger on the trigger or squeezing with your thumb.'
        : 'Not enough finger on the trigger causes shots to the left.',
      recommendations: [
        'Check trigger finger placement',
        'Use the pad of your finger, not the joint',
        'Keep trigger finger isolated from grip',
        'Maintain consistent finger position',
      ],
    },
    {
      id: 'lowRight',
      label: r ? 'Tightening Grip While Pulling Trigger' : 'Heeling (Anticipating Recoil)',
      shortLabel: r ? 'Low\nRight' : 'Low\nLeft',
      reason: r
        ? 'Usually from tightening your grip as you pull the trigger.'
        : 'Often caused by anticipating recoil and pushing the heel of your palm forward.',
      recommendations: [
        'Maintain consistent grip pressure',
        "Don't squeeze harder during trigger press",
        'Practice grip isolation exercises',
        'Focus on support hand pressure',
      ],
    },
    {
      id: 'low',
      label: 'Breaking Wrist Down, Pushing Forward, Relaxing Hand Too Soon, or Dropping Head',
      shortLabel: 'Low',
      reason:
        'Can be caused by breaking wrist down, pushing forward, relaxing your hand too soon, or dropping your head.',
      recommendations: [
        'Keep wrists locked',
        'Maintain head position through the shot',
        'Follow through completely',
        "Don't anticipate the shot",
      ],
    },
    {
      id: 'lowLeft',
      label: r ? 'Jerking or Slapping Trigger' : 'Squeezing Fingers',
      shortLabel: r ? 'Low\nLeft' : 'Low\nRight',
      reason: r
        ? 'Most commonly caused by jerking or slapping the trigger.'
        : 'Usually from squeezing your fingers during the trigger press.',
      recommendations: [
        'Practice smooth, steady trigger press',
        'Use dry fire practice extensively',
        'Focus on trigger reset',
        'Try ball-and-dummy drills to identify flinching',
      ],
      drills: [
        { title: 'Trigger Control Drill' },
        { title: 'Flinch Mitigation Drill' },
      ],
    },
    {
      id: 'left',
      label: r
        ? 'Too Little Fingertip on Trigger'
        : 'Squeezing Thumb or Too Much Fingertip on Trigger',
      shortLabel: r ? 'Left' : 'Right',
      reason: r
        ? 'Not enough finger on the trigger causes shots to the left.'
        : 'Caused by too much finger on the trigger or squeezing with your thumb.',
      recommendations: [
        'Adjust trigger finger placement',
        'Use more of your finger pad',
        'Keep thumb relaxed',
        'Practice consistent trigger finger position',
      ],
    },
    {
      id: 'highLeft',
      label: r
        ? 'Pushing (Anticipating Recoil) or No Follow Through'
        : 'Squeezing Fingers',
      shortLabel: r ? 'High\nLeft' : 'High\nRight',
      reason: r
        ? 'Usually from pushing in anticipation of recoil or not following through.'
        : 'Often from squeezing fingers as you fire.',
      recommendations: [
        'Focus on complete follow-through',
        "Don't anticipate the shot",
        'Keep pressing through the shot',
        'Maintain sight picture during recoil',
      ],
    },
  ];
};

// ─── Component ───────────────────────────────────────────────────────────────

const ShotDiagnostics = () => {
  const [handedness, setHandedness] = useState('right');
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const sections = getTargetSections(handedness);
  const sectionMap = Object.fromEntries(sections.map((s) => [s.id, s]));
  const selected = selectedId ? sectionMap[selectedId] : null;

  const handleHandedness = (hand) => {
    setHandedness(hand);
    setSelectedId(null);
  };

  return (
    <Layout>
      <section id="wrapper">
        <div className="inner" style={{ paddingTop: '40px' }}>

          {/* Title */}
          <h2
            className="major"
            style={{
              textAlign: 'center',
              marginBottom: '6px',
              fontSize: 'clamp(1.5rem, 5vw, 2.35rem)',
            }}
          >
            Shot Diagnostics
          </h2>
          <p style={{
            textAlign: 'center',
            fontSize: '0.72em',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            opacity: '0.6',
            margin: '0 0 16px',
          }}>
            Patent Pending
          </p>
          <p style={{
            textAlign: 'center',
            fontSize: '1em',
            opacity: '0.75',
            maxWidth: '520px',
            margin: '0 auto 28px',
            lineHeight: '1.6',
          }}>
            Click a zone on the target where your shots are landing to get diagnostics and
            improvement tips.
          </p>

          {/* Handedness Toggle */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '28px' }}>
            {['right', 'left'].map((hand) => (
              <button
                key={hand}
                type="button"
                onClick={() => handleHandedness(hand)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  padding: '9px 22px',
                  borderRadius: '20px',
                  border: handedness === hand ? '2px solid #FF5722' : '2px solid rgba(255,255,255,0.2)',
                  backgroundColor: handedness === hand ? '#FF5722' : 'rgba(255,255,255,0.05)',
                  color: '#fff',
                  fontSize: '0.9em',
                  fontWeight: '600',
                  lineHeight: 1.2,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  outline: 'none',
                }}
              >
                {hand === 'right' ? 'Right-Handed' : 'Left-Handed'}
              </button>
            ))}
          </div>

          {/* SVG Target */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginBottom: '32px',
          }}>
            <div style={{
              backgroundColor: 'rgba(27, 31, 34, 0.9)',
              borderRadius: '16px',
              padding: '5px',
              border: '2px solid rgba(255,255,255,0.1)',
              boxShadow: '0 6px 24px rgba(0,0,0,0.4)',
              display: 'inline-block',
            }}>
              <svg
                width="400"
                height="400"
                viewBox="0 0 320 320"
                style={{ display: 'block' }}
              >
                {/* Outer ring sections */}
                {SECTIONS_META.map((meta) => {
                  const isSelected = selectedId === meta.id;
                  const sectionData = sectionMap[meta.id];
                  const labelLines = sectionData.shortLabel.split('\n');
                  const labelPos = getLabelPos(meta.centerAngle);

                  return (
                    <g
                      key={meta.id}
                      onClick={() => setSelectedId(isSelected ? null : meta.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <path
                        d={createRadialSectionPath(CX, CY, INNER_R, OUTER_R, meta.start, meta.end)}
                        fill={isSelected ? '#FF5722' : '#FFFFFF'}
                        stroke="#FF5722"
                        strokeWidth={isSelected ? 3 : 1.5}
                        opacity={isSelected ? 1 : 0.92}
                        style={{ transition: 'fill 0.15s ease' }}
                      />
                      {/* Section label */}
                      <text
                        x={labelPos.x}
                        y={labelPos.y - (labelLines.length > 1 ? 7 : 0)}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize="10"
                        fontWeight="700"
                        fill={isSelected ? '#fff' : '#1C1C1E'}
                        style={{ pointerEvents: 'none', userSelect: 'none' }}
                      >
                        {labelLines.map((line, i) => (
                          <tspan key={i} x={labelPos.x} dy={i === 0 ? 0 : 14}>
                            {line}
                          </tspan>
                        ))}
                      </text>
                    </g>
                  );
                })}

                {/* Inner rings (bullseye) */}
                <circle cx={CX} cy={CY} r={INNER_R} fill="#2C2C2E" stroke="#FF5722" strokeWidth="2" />
                <circle cx={CX} cy={CY} r={35} fill="#1C1C1E" stroke="#FF5722" strokeWidth="1.5" />
                <circle cx={CX} cy={CY} r={14} fill="#FF5722" opacity="0.85" />
              </svg>
            </div>

            {!selected && (
              <p style={{ marginTop: '14px', fontSize: '0.88em', opacity: '0.5', textAlign: 'center' }}>
                👆 Click any section to get started
              </p>
            )}
          </div>

          {/* Diagnostic Card */}
          {selected && (
            <div style={{
              maxWidth: '640px',
              margin: '0 auto 50px',
              backgroundColor: 'rgba(27, 31, 34, 0.9)',
              borderRadius: '14px',
              padding: '28px',
              border: '2px solid rgba(255, 87, 34, 0.25)',
              boxShadow: '0 6px 24px rgba(0,0,0,0.3)',
            }}>
              {/* Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '20px',
                paddingBottom: '16px',
                borderBottom: '1px solid rgba(255,255,255,0.1)',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flex: 1, minWidth: 0 }}>
                  <IoAlertCircle size={24} color="#FF5722" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span style={{ fontSize: '1.05em', fontWeight: 'bold', color: '#fff', lineHeight: '1.4' }}>
                    {selected.label}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedId(null)}
                  aria-label="Close"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    flexShrink: 0,
                    lineHeight: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <IoCloseCircle size={28} color="#FF5722" />
                </button>
              </div>

              {/* Why This Happens */}
              <p style={{ fontSize: '0.88em', fontWeight: 'bold', color: '#FF5722', marginBottom: '8px', marginTop: '0' }}>
                WHY THIS HAPPENS
              </p>
              <p style={{ fontSize: '0.95em', color: '#fff', lineHeight: '1.7', marginBottom: '20px' }}>
                {selected.reason}
              </p>

              {/* How to Fix It */}
              <p style={{ fontSize: '0.88em', fontWeight: 'bold', color: '#FF5722', marginBottom: '10px' }}>
                HOW TO FIX IT
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px' }}>
                {selected.recommendations.map((rec, i) => (
                  <li key={i} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    padding: '6px 0',
                    borderBottom: i < selected.recommendations.length - 1
                      ? '1px solid rgba(255,255,255,0.07)'
                      : 'none',
                  }}>
                    <span style={{ color: '#FF5722', fontSize: '1.2em', lineHeight: '1.4', flexShrink: 0 }}>•</span>
                    <span style={{ fontSize: '0.95em', color: '#fff', lineHeight: '1.6' }}>{rec}</span>
                  </li>
                ))}
              </ul>

              {/* Drills (optional) */}
              {/* Redirect users to the app for practice drills */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '9px', 
                color: '#FF5722', 
                fontSize: '0.93em', 
                opacity: 0.8, 
                margin: '8px 0 18px 0', 
                paddingLeft: '2px'
              }}>
                <IoPlayCircle size={21} color="#FF5722" style={{ flexShrink: 0, marginTop: '1px' }} />
                <span>
                  For app-based practice drills, try the <a 
                    href="https://rangedrills.io/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    style={{ color: '#FF5722', textDecoration: 'underline', fontWeight: 500 }}
                  >Range Drills app</a>.
                </span>
              </div>
         
         
            </div>
          )}

          {/* Marketing Banner */}
          <div style={{
            maxWidth: '640px',
            margin: '0 auto 50px',
            padding: '32px 28px',
            backgroundColor: 'rgba(255, 87, 34, 0.08)',
            borderRadius: '14px',
            border: '1px solid rgba(255, 87, 34, 0.3)',
            textAlign: 'center',
          }}>
            <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'center' }}>
              <img
                src={logo}
                alt="Range Drills"
                style={{ height: '52px', width: 'auto', objectFit: 'contain' }}
              />
            </div>
            <h3 style={{ marginTop: '0', marginBottom: '10px', color: '#FF5722', fontSize: '1.2em' }}>
              This is just the beginning
            </h3>
            <p style={{ fontSize: '0.97em', lineHeight: '1.7', opacity: '0.9', marginBottom: '20px' }}>
              Shot Diagnostics is one small tool Range Drills puts in your hands — for free.
              The app goes deeper: structured drills, video breakdowns, progress tracking, and
              personalized coaching to make every range session count.
            </p>
            <p style={{ fontSize: '0.9em', opacity: '0.7', marginBottom: '24px' }}>
              Start unlocking your true potential at the range.
         
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <a
                href="https://apps.apple.com/us/app/range-drills/id6738703801"
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-block', transition: 'transform 0.2s' }}
                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/apple-download-logo.svg`}
                  alt="Download on the App Store"
                  style={{ height: '48px', width: 'auto' }}
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=io.idodev.rangedrills"
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-block', transition: 'transform 0.2s' }}
                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/GetItOnGooglePlay_Badge_Web_color_English.svg`}
                  alt="Get it on Google Play"
                  style={{ height: '48px', width: 'auto' }}
                />
              </a>
            </div>
          </div>

        </div>
      </section>
    </Layout>
  );
};

export default ShotDiagnostics;
